import type { ConsumerApiClient } from '../client';

export interface PeliPointsBalance {
  balance: number;
  lifetimeEarned: number;
  lifetimeRedeemed: number;
}

export interface PeliPointsTransaction {
  id: string;
  type: 'earned' | 'redeemed' | 'expired' | 'bonus' | 'manual_add' | 'manual_subtract';
  points: number;
  description: string;
  createdAt: string;
  balanceAfter: number;
}

export interface Reward {
  id: string;
  name: string;
  description: string | null;
  type: string;
  pointsCost: number;
  imageUrl?: string | null;
  stockQuantity?: number | null;
}

export interface Redemption {
  id: string;
  rewardName: string;
  rewardType: string;
  pointsSpent: number;
  status: string;
  createdAt: string;
  fulfilledAt: string | null;
}

export interface PeliPointsHistory {
  transactions: PeliPointsTransaction[];
  total: number;
}

export interface RedeemRewardResponse {
  success: boolean;
  remainingBalance: number;
  rewardName: string;
}

export interface ClaimBonusResponse {
  success: boolean;
  pointsAwarded: number;
  newBalance: number;
}

export interface PeliPointsApi {
  getBalance(): Promise<PeliPointsBalance>;
  getHistory(page?: number): Promise<PeliPointsHistory>;
  getRewards(): Promise<Reward[]>;
  redeemReward(rewardId: string): Promise<RedeemRewardResponse>;
  getRedemptions(): Promise<Redemption[]>;
  /**
   * Claim a one-time bonus (e.g. enabling push notifications during onboarding).
   * Backend validates the bonusType and prevents double-claiming.
   * Returns 409 if already claimed — callers should handle gracefully.
   */
  claimBonus(bonusType: string): Promise<ClaimBonusResponse>;
}

export function createPelipointsApi(client: ConsumerApiClient): PeliPointsApi {
  return {
    async getBalance() {
      const data = await client.get<any>('/pelipoints');
      return {
        balance: data.balance || 0,
        lifetimeEarned: data.lifetimeEarned || 0,
        lifetimeRedeemed: data.lifetimeRedeemed || 0,
      };
    },

    async getHistory(page = 1) {
      const data = await client.get<any>(`/pelipoints/history?page=${page}`);
      const raw = data.transactions || data.entries || [];
      const txs: PeliPointsTransaction[] = raw.map((tx: any) => ({
        id: tx.id,
        type: tx.type,
        points: tx.points,
        description: tx.description || tx.adminNote || tx.source || tx.type,
        createdAt: tx.createdAt,
        balanceAfter: tx.balanceAfter,
      }));
      return { transactions: txs, total: txs.length };
    },

    async getRewards() {
      const data = await client.get<any>('/pelipoints/rewards');
      const list = Array.isArray(data) ? data : data.rewards || [];
      return list.map((r: any) => ({
        id: r.id,
        name: r.name,
        description: r.description,
        type: r.type || r.rewardType,
        pointsCost: r.pointsCost,
        imageUrl: r.imageUrl,
        stockQuantity: r.stockQuantity,
      }));
    },

    redeemReward(rewardId) {
      return client.post('/pelipoints/redeem', { rewardId });
    },

    getRedemptions() {
      return client.get('/pelipoints/redemptions');
    },

    claimBonus(bonusType) {
      return client.post('/pelipoints/claim-bonus', { bonusType });
    },
  };
}
